/** The assignable-people directory.
 *
 * Distinct from `useAdminUsers`, which reads `/admin/users` and therefore returns nothing
 * unless the signed-in person is an Administrator. Anything that offers a picker to a
 * Portfolio Manager or a Team Member has to read this instead.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface Person {
  id: string
  displayName: string
  email: string
  /** A vendor's engineer or other outside contributor. Labelled wherever they appear. */
  isExternal: boolean
  /** Points a fortnight this person can take on. */
  sprintCapacity: number
  /** True while they are on the default (16) rather than a number of their own. */
  capacityIsDefault: boolean
}

export function usePeople() {
  return useQuery({
    queryKey: ['people'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/people')
      return (data as unknown as { people: Person[] }).people
    },
    // The directory changes when somebody joins or leaves, not while a dialog is open.
    staleTime: 5 * 60 * 1000,
  })
}

/** Set someone's sprint capacity; `null` puts them back on the default. PM/Admin only. */
export function useSetCapacity() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, points }: { userId: string; points: number | null }) => {
      await api.PUT('/api/v1/people/{user_id}/capacity', {
        params: { path: { user_id: userId } },
        body: { points } as never,
      })
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['people'] }),
  })
}
